// Third party imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

// App imports
import { client } from 'src/utils/api';
import en from 'src/messages/en.json';
import { setError } from 'src/redux/features/common/commonSlice';
// import {
//   deserialiseTimesheet,
//   generateTimesheetResponse,
// } from 'src/utils/generateTimesheet';

export const getTimesheetByUser: any = createAsyncThunk(
  'timesheet/getTimesheetByUser',
  async ({ resource, date }: any, { signal, rejectWithValue, dispatch }) => {
    try {
      const response = await client.get(`Timesheet/${date}/${resource}`, {
        signal,
      });

      // const isDateInPresentWeek = isDateInCurrentWeek(date);
      // const isDateInFuture = isFutureDate(date);

      // if (!response || !response.data || !response.data.projects.length) {
      //   const timesheetResponse = generateTimesheetResponse(projects, date);
      //   const timesheet = deserialiseTimesheet(timesheetResponse);

      //   return timesheet;
      // }

      return response.data;
    } catch (err) {
      if (err === en.Common.Error.signalAborted) {
        return;
        // aborted → just ignore
      }
      dispatch(setError(en.Timesheet.Error.fetchTimesheet));

      return rejectWithValue(err);
    }
  },
);

export const saveTimesheet: any = createAsyncThunk(
  'timesheet/saveTimesheet',
  async ({ date, resource, payload }: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await client.post(
        `Timesheet/${date}/${resource}`,
        payload,
      );

      toast.success(en.Timesheet.Success.timesheetSaved);

      return response.data;
    } catch (err) {
      dispatch(
        setError(err?.customizedMessage || en.Timesheet.Error.saveTimesheet),
      );

      return rejectWithValue(err);
    }
  },
);

export const submitTimesheet: any = createAsyncThunk(
  'timesheet/submitTimesheet',
  async (
    { date, resource, payload, message }: any,
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await client.post(
        `Timesheet/${date}/${resource}/submit`,
        payload,
      );

      toast.success(message ?? en.Timesheet.Success.timesheetSubmitted);

      return response.data;
    } catch (err) {
      dispatch(
        setError(err?.customizedMessage || en.Timesheet.Error.submitTimesheet),
      );

      return rejectWithValue(err);
    }
  },
);

export const getTimesheetHistory: any = createAsyncThunk(
  'timesheet/getTimeSheetHistory',
  async ({ resource, date }: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await client.get(
        `Timesheet/${date}/${resource}/GetTimeSheetHistory`,
      );

      return response.data;
    } catch (err) {
      dispatch(setError(en.Timesheet.Error.timesheetHistory));

      return rejectWithValue(err);
    }
  },
);

export const getUserDataByUser: any = createAsyncThunk(
  'timesheet/getUserDataByUser',
  async (
    { resource, date, showActiveOnly, showDirectOnly }: any,
    { rejectWithValue, dispatch },
  ) => {
    try {
      const queryParams = new URLSearchParams({
        showActiveOnly: String(showActiveOnly),
        showDirectOnly: String(showDirectOnly),
      });

      const response = await client.get(
        `User/${date}/${resource}?${queryParams}`,
      );

      return response.data;
    } catch (err) {
      dispatch(setError(en.Common.Error.fetchData));

      return rejectWithValue(err);
    }
  },
);

export const getAndViewTimesheetByUser: any = createAsyncThunk(
  'timesheet/getAndViewTimesheetByUser',
  async ({ resource, date }: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await client.get(
        `Timesheet/project/${date}/${resource}`,
      );

      return response.data;
    } catch (err) {
      dispatch(setError(en.Common.Error.fetchData));

      return rejectWithValue(err);
    }
  },
);
export const approveReporteesTimesheet: any = createAsyncThunk(
  'timesheet/approveReporteesTimesheet',
  async (
    {
      approverEmail,
      status,
      date,
      resources,
      showActiveOnly,
      showDirectOnly,
    }: any,
    { rejectWithValue, dispatch },
  ) => {
    try {
      const requestBody = {
        approverEmail,
        status,
        date,
        resources,
        showActiveOnly,
        showDirectOnly,
      };

      const response = await client.post('Timesheet/BulkApproval', requestBody);

      return response.data;
    } catch (err) {
      dispatch(setError(en.Timesheet.Error.approveTimesheet));

      return rejectWithValue(err);
    }
  },
);

enum Status {
  idle = 'idle',
  loading = 'loading',
  succeeded = 'succeeded',
  failed = 'failed',
}

const timesheetSlice = createSlice({
  name: 'timesheet',
  initialState: {
    data: {},
    history: [],
    error: '',
    status: Status.idle,
    loading: false,
    historyLoading: false,
  },
  reducers: {
    resetTimesheet: (state) => {
      state.data = {
        data: {},
        history: [],
        error: '',
        status: Status.idle,
        historyLoading: false,
      };
    },
  },
  extraReducers: {
    [saveTimesheet.pending]: (state) => {
      state.loading = true;
      state.status = Status.loading;
    },
    [saveTimesheet.fulfilled]: (state) => {
      state.loading = false;
      // state.data = action.payload;
      state.status = Status.succeeded;
    },
    [saveTimesheet.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error.message;
      state.status = Status.failed;
    },
    [submitTimesheet.pending]: (state) => {
      state.loading = true;
      state.status = Status.loading;
    },
    [submitTimesheet.fulfilled]: (state, action) => {
      state.loading = false;
      // state.data = action.payload;
      state.status = Status.succeeded;
    },
    [submitTimesheet.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error.message;
      state.status = Status.failed;
    },

    [getTimesheetByUser.pending]: (state) => {
      state.loading = true;
      state.status = Status.loading;
    },
    [getTimesheetByUser.fulfilled]: (state, action) => {
      state.loading = false;
      // Deserialse the response here
      state.data = action.payload;
      state.status = Status.succeeded;
    },
    [getTimesheetByUser.rejected]: (state, action) => {
      state.loading = false;
      state.status = Status.failed;
      state.data = {};
      state.error = action.error.message;
    },

    [getTimesheetHistory.pending]: (state) => {
      state.historyLoading = true;
      state.status = Status.loading;
    },
    [getTimesheetHistory.fulfilled]: (state, action) => {
      state.historyLoading = false;
      state.history = action.payload;
      state.status = Status.succeeded;
    },
    [getTimesheetHistory.rejected]: (state, action) => {
      state.historyLoading = false;
      state.status = Status.failed;
      state.error = action.error.message;
    },
    [getUserDataByUser.pending]: (state) => {
      state.loading = true;
      state.status = Status.loading;
    },
    [getUserDataByUser.fulfilled]: (state, action) => {
      state.loading = false;
      state.data = action.payload;
      state.status = Status.succeeded;
    },
    [getUserDataByUser.rejected]: (state, action) => {
      state.loading = false;
      state.status = Status.failed;
      state.data = {};
      state.error = action.error.message;
    },
    [getAndViewTimesheetByUser.pending]: (state) => {
      state.loading = true;
      state.status = Status.loading;
    },
    [getAndViewTimesheetByUser.fulfilled]: (state, action) => {
      state.loading = false;
      state.data = action.payload;
      state.status = Status.succeeded;
    },
    [getAndViewTimesheetByUser.rejected]: (state, action) => {
      state.loading = false;
      state.status = Status.failed;
      state.data = {};
      state.error = action.error.message;
    },
    [approveReporteesTimesheet.pending]: (state) => {
      state.loading = true;
      state.status = Status.loading;
    },
    [approveReporteesTimesheet.fulfilled]: (state, action) => {
      state.loading = false;
      state.data = action.payload;
      state.status = Status.succeeded;
    },
    [approveReporteesTimesheet.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error.message;
      state.status = Status.failed;
    },
  },
});

export const getTimesheet = (state) => state.timesheet;

export const getTimesheetHistoryData = (state) => state.timesheet;

const actions = timesheetSlice.actions;

export { actions };
export default timesheetSlice.reducer;
